import type {
  GamePlatform,
  LabBuild,
  LabCredit,
  LabQuizDraft,
  LabQuizQuestion,
  LabReleaseStatus,
  LabShowcaseState,
  LabSourcePackage,
  LabTeamRole,
  LabUnit,
  LabVisibility,
  User,
} from '@/lib/types';

export const DEMO_EMAIL = 'demo@ludolio.com';
export const DEMO_ORG_SLUG = 'demo';

const ORG_ID = 'demo-org';
const USER_ID = 'demo-user';

export interface DemoPerson {
  id: string;
  name: string;
  email: string;
}

export interface DemoTeam {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  members: Array<{ userId: string; role: LabTeamRole }>;
}

export interface DemoRelease {
  id: string;
  version: string;
  status: LabReleaseStatus;
  visibility: LabVisibility;
  title: string;
  description: string | null;
  credits: LabCredit[];
  contentNotes: string | null;
  iconUrl: string | null;
  bannerUrl: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  remixPermission: 'none' | 'organization';
  internalRemixTermsVersion: string | null;
  assetSharingConfirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
  builds: LabBuild[];
  sourcePackage: LabSourcePackage | null;
}

export interface DemoQuizResponse {
  id: string;
  submittedAt: string;
  updatedAt: string;
  respondent: { name: string; email: string };
  answers: Array<{ questionId: string; prompt: string; answer: string }>;
}

export interface DemoQuizVersion {
  id: string;
  version: number;
  description: string | null;
  questions: LabQuizQuestion[];
  createdAt: string;
  responses: DemoQuizResponse[];
}

export interface DemoQuiz {
  id: string;
  projectId: string;
  title: string;
  status: 'draft' | 'open' | 'closed';
  updatedAt: string;
  draft: LabQuizDraft;
  currentVersionId: string | null;
  versions: DemoQuizVersion[];
}

export interface DemoProject {
  id: string;
  teamId: string;
  unitId: string | null;
  appId: number | null;
  isDrmFree: boolean;
  createdAt: string;
  updatedAt: string;
  parentReleaseId: string | null;
  releases: DemoRelease[];
  quizzes: DemoQuiz[];
  showcase: {
    submission: LabShowcaseState['submission'];
  };
}

export interface DemoCatalogRelease {
  projectId: string;
  releaseId: string;
  appId: number;
  title: string;
  description: string | null;
  version: string;
  visibility: LabVisibility;
  publishedAt: string;
  unit: { id: string; code: string; name: string } | null;
  platforms: GamePlatform[];
  remixable: boolean;
  credits: LabCredit[];
  contentNotes: string | null;
  parentReleaseId: string | null;
  withdrawn: boolean;
}

export interface DemoState {
  user: User;
  organization: { id: string; name: string; slug: string };
  units: LabUnit[];
  people: DemoPerson[];
  teams: DemoTeam[];
  projects: DemoProject[];
  catalog: DemoCatalogRelease[];
}

export const DEMO_TERMS = {
  version: '1',
  text: [
    'Members of this university may download this source, change it, and publish a derivative inside the same university. The derivative keeps attribution and the link to this version.',
    'These terms do not allow sharing the source or its assets outside the university. Publishing beyond the university needs a separate licence.',
  ].join('\n\n'),
};

export const SOFTWARE_LICENSES = [
  { id: 'MIT', name: 'MIT' },
  { id: 'Apache-2.0', name: 'Apache 2.0' },
  { id: 'BSD-2-Clause', name: 'BSD 2-Clause' },
  { id: 'BSD-3-Clause', name: 'BSD 3-Clause' },
  { id: 'ISC', name: 'ISC' },
  { id: 'MPL-2.0', name: 'Mozilla Public License 2.0' },
  { id: 'GPL-3.0-only', name: 'GPL 3.0 only' },
  { id: 'LGPL-3.0-only', name: 'LGPL 3.0 only' },
  { id: 'AGPL-3.0-only', name: 'AGPL 3.0 only' },
  { id: 'Unlicense', name: 'The Unlicense' },
  { id: 'CC0-1.0', name: 'CC0 1.0' },
];

export const ASSET_LICENSES = [
  { id: 'CC-BY-4.0', name: 'CC BY 4.0' },
  { id: 'CC-BY-SA-4.0', name: 'CC BY-SA 4.0' },
  { id: 'CC0-1.0', name: 'CC0 1.0' },
  { id: 'MIT', name: 'MIT' },
  { id: 'ownership', name: 'The team owns these assets' },
];

const daysAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString();

function passedBuild(id: string, platform: GamePlatform, fileSize: number, executablePath: string, createdAt: string): LabBuild {
  return {
    id,
    platform,
    status: 'passed',
    fileSize,
    failureReason: null,
    executablePath,
    executableCandidates: [executablePath],
    createdAt,
    scannedAt: createdAt,
  };
}

function passedSource(
  id: string,
  engine: LabSourcePackage['engine'],
  engineVersion: string,
  fileSize: number,
  fileCount: number,
  createdAt: string,
): LabSourcePackage {
  return {
    id,
    status: 'passed',
    fileSize,
    failureReason: null,
    engine,
    engineVersion,
    fileCount,
    structureConfirmedAt: null,
    createdAt,
    scannedAt: createdAt,
  };
}

export function createDemoState(): DemoState {
  const game101: LabUnit = {
    id: 'unit-game101',
    code: 'GAME101',
    name: 'Introduction to Game Design',
    year: 2026,
    semester: 'semester_1',
  };
  const game210: LabUnit = {
    id: 'unit-game210',
    code: 'GAME210',
    name: 'Level Design Studio',
    year: 2026,
    semester: 'semester_1',
  };

  const crossing: LabQuizQuestion = {
    id: 'q-harbor-1',
    kind: 'single_choice',
    prompt: 'How long did the crossing take?',
    required: true,
    options: [
      { id: 'q-harbor-1a', label: 'Under ten minutes' },
      { id: 'q-harbor-1b', label: 'About half an hour' },
      { id: 'q-harbor-1c', label: 'I did not finish' },
    ],
  };
  const sounds: LabQuizQuestion = {
    id: 'q-harbor-2',
    kind: 'multi_choice',
    prompt: 'Which sounds did you notice?',
    required: true,
    options: [
      { id: 'q-harbor-2a', label: 'Foghorn' },
      { id: 'q-harbor-2b', label: 'Water' },
      { id: 'q-harbor-2c', label: 'Engine' },
      { id: 'q-harbor-2d', label: 'None of these' },
    ],
  };
  const change: LabQuizQuestion = {
    id: 'q-harbor-3',
    kind: 'short_text',
    prompt: 'What would you change before the next playtest?',
    required: true,
  };
  const harborQuestions = [crossing, sounds, change];
  const harborDescription = 'Tell us what you noticed in Harbor Light. Your answers stay with the project team.';

  const response = (
    id: string,
    name: string,
    email: string,
    days: number,
    crossingAnswer: string,
    soundsAnswer: string,
    note: string,
  ): DemoQuizResponse => ({
    id,
    submittedAt: daysAgo(days),
    updatedAt: daysAgo(days),
    respondent: { name, email },
    answers: [
      { questionId: crossing.id, prompt: crossing.prompt, answer: crossingAnswer },
      { questionId: sounds.id, prompt: sounds.prompt, answer: soundsAnswer },
      { questionId: change.id, prompt: change.prompt, answer: note },
    ],
  });

  const harborPublishedAt = daysAgo(12);
  const harborSourceAt = daysAgo(20);

  return {
    user: {
      id: USER_ID,
      email: DEMO_EMAIL,
      firstName: 'Demo',
      lastName: 'Student',
      role: 'student',
      orgId: ORG_ID,
      needsOnboarding: false,
      needsOrgNoticeConsent: false,
    },
    organization: { id: ORG_ID, name: 'Northbridge University', slug: DEMO_ORG_SLUG },
    units: [game101, game210],
    people: [
      { id: USER_ID, name: 'Demo Student', email: DEMO_EMAIL },
      { id: 'user-casey', name: 'Casey Morgan', email: 'casey.morgan@demo.ludolio.com' },
      { id: 'user-jordan', name: 'Jordan Lee', email: 'jordan.lee@demo.ludolio.com' },
      { id: 'user-avery', name: 'Avery Patel', email: 'avery.patel@demo.ludolio.com' },
      { id: 'user-sam', name: 'Sam Nguyen', email: 'sam.nguyen@demo.ludolio.com' },
    ],
    teams: [
      {
        id: 'team-pixel',
        name: 'Pixel Team',
        description: 'A studio group for GAME101.',
        createdAt: daysAgo(40),
        updatedAt: daysAgo(2),
        members: [
          { userId: USER_ID, role: 'owner' },
          { userId: 'user-casey', role: 'manager' },
          { userId: 'user-jordan', role: 'member' },
        ],
      },
      {
        id: 'team-field',
        name: 'Field Notes',
        description: 'Shared experiments for GAME210.',
        createdAt: daysAgo(30),
        updatedAt: daysAgo(6),
        members: [
          { userId: 'user-avery', role: 'owner' },
          { userId: USER_ID, role: 'member' },
          { userId: 'user-sam', role: 'member' },
        ],
      },
    ],
    projects: [
      {
        id: 'project-harbor',
        teamId: 'team-pixel',
        unitId: game101.id,
        appId: 48121,
        isDrmFree: false,
        createdAt: daysAgo(40),
        updatedAt: daysAgo(2),
        parentReleaseId: null,
        releases: [
          {
            id: 'rel-harbor-12',
            version: '1.2.0',
            status: 'published',
            visibility: 'organization',
            title: 'Harbor Light',
            description: 'A short crossing at dusk. Listen for the horn, then find the gate before the tide turns.',
            credits: [
              { name: 'Demo Student', contribution: 'Design' },
              { name: 'Casey Morgan', contribution: 'Sound' },
              { name: 'Jordan Lee', contribution: 'Art' },
            ],
            contentNotes: 'Dark scenes and a loud horn.',
            iconUrl: null,
            bannerUrl: null,
            submittedAt: daysAgo(14),
            reviewedAt: harborPublishedAt,
            reviewNote: null,
            publishedAt: harborPublishedAt,
            remixPermission: 'organization',
            internalRemixTermsVersion: DEMO_TERMS.version,
            assetSharingConfirmedAt: harborSourceAt,
            createdAt: daysAgo(18),
            updatedAt: harborPublishedAt,
            builds: [passedBuild('build-harbor', 'windows-x64', 186_000_000, 'HarborLight.exe', daysAgo(18))],
            sourcePackage: passedSource('source-harbor', 'godot', '4.3', 42_000_000, 128, harborSourceAt),
          },
          {
            id: 'rel-harbor-10',
            version: '1.0.0',
            status: 'superseded',
            visibility: 'unit',
            title: 'Harbor Light',
            description: 'The first playable crossing.',
            credits: [{ name: 'Demo Student', contribution: 'Design' }],
            contentNotes: null,
            iconUrl: null,
            bannerUrl: null,
            submittedAt: daysAgo(28),
            reviewedAt: daysAgo(26),
            reviewNote: null,
            publishedAt: daysAgo(26),
            remixPermission: 'none',
            internalRemixTermsVersion: null,
            assetSharingConfirmedAt: null,
            createdAt: daysAgo(34),
            updatedAt: daysAgo(26),
            builds: [],
            sourcePackage: null,
          },
        ],
        quizzes: [
          {
            id: 'quiz-harbor',
            projectId: 'project-harbor',
            title: 'After you play',
            status: 'open',
            updatedAt: daysAgo(3),
            draft: { description: harborDescription, questions: harborQuestions },
            currentVersionId: 'quiz-harbor-v1',
            versions: [
              {
                id: 'quiz-harbor-v1',
                version: 1,
                description: harborDescription,
                questions: harborQuestions,
                createdAt: daysAgo(8),
                responses: [
                  response('resp-maya', 'Maya Chen', 'maya.chen@demo.ludolio.com', 4, 'About half an hour', 'Foghorn, Water', 'The dock could use a clearer exit.'),
                  response('resp-luis', 'Luis Ortega', 'luis.ortega@demo.ludolio.com', 5, 'Under ten minutes', 'Water', 'I wanted a map of the shore.'),
                  response('resp-priya', 'Priya Shah', 'priya.shah@demo.ludolio.com', 6, 'I did not finish', 'Engine', 'The first puzzle stopped me.'),
                  response('resp-noah', 'Noah Kim', 'noah.kim@demo.ludolio.com', 7, 'About half an hour', 'Foghorn, Water, Engine', 'Leave more time between the horn and the gate.'),
                ],
              },
            ],
          },
        ],
        showcase: {
          submission: {
            id: 'showcase-harbor',
            status: 'draft',
            slug: 'harbor-light',
            openSourceLicense: 'MIT',
            assetLicense: 'CC-BY-4.0',
            assetDeclaration: null,
            sourcePublished: false,
            reviewNote: null,
            submittedAt: null,
            reviewedAt: null,
          },
        },
      },
      {
        id: 'project-signal',
        teamId: 'team-pixel',
        unitId: game210.id,
        appId: null,
        isDrmFree: true,
        createdAt: daysAgo(9),
        updatedAt: daysAgo(1),
        parentReleaseId: null,
        releases: [
          {
            id: 'rel-signal',
            version: '0.3.0',
            status: 'draft',
            visibility: 'unit',
            title: 'Signal Garden',
            description: 'A garden of switches. Each one changes the path, and none of them reset on their own.',
            credits: [
              { name: 'Demo Student', contribution: 'Design' },
              { name: 'Casey Morgan', contribution: 'Level design' },
            ],
            contentNotes: null,
            iconUrl: null,
            bannerUrl: null,
            submittedAt: null,
            reviewedAt: null,
            reviewNote: null,
            publishedAt: null,
            remixPermission: 'none',
            internalRemixTermsVersion: null,
            assetSharingConfirmedAt: null,
            createdAt: daysAgo(9),
            updatedAt: daysAgo(1),
            builds: [],
            sourcePackage: null,
          },
        ],
        quizzes: [
          {
            id: 'quiz-signal',
            projectId: 'project-signal',
            title: 'First impressions',
            status: 'draft',
            updatedAt: daysAgo(1),
            draft: {
              description: 'A few questions for the next class playtest.',
              questions: [
                {
                  id: 'q-signal-1',
                  kind: 'short_text',
                  prompt: 'Where did you get stuck?',
                  required: true,
                },
              ],
            },
            currentVersionId: null,
            versions: [],
          },
        ],
        showcase: { submission: null },
      },
      {
        id: 'project-lantern',
        teamId: 'team-field',
        unitId: game210.id,
        appId: 48122,
        isDrmFree: false,
        createdAt: daysAgo(16),
        updatedAt: daysAgo(6),
        parentReleaseId: 'rel-tide',
        releases: [
          {
            id: 'rel-lantern',
            version: '1.0.0',
            status: 'published',
            visibility: 'organization',
            title: 'Lantern Echo',
            description: 'A remix of Tide Pool. The lantern keeps the last path you walked.',
            credits: [
              { name: 'Tide Pool', contribution: 'Original 1.1.0', permanent: true },
              { name: 'Avery Patel', contribution: 'Remix' },
            ],
            contentNotes: null,
            iconUrl: null,
            bannerUrl: null,
            submittedAt: daysAgo(8),
            reviewedAt: daysAgo(6),
            reviewNote: null,
            publishedAt: daysAgo(6),
            remixPermission: 'organization',
            internalRemixTermsVersion: DEMO_TERMS.version,
            assetSharingConfirmedAt: daysAgo(10),
            createdAt: daysAgo(11),
            updatedAt: daysAgo(6),
            builds: [passedBuild('build-lantern', 'mac-arm64', 210_000_000, 'Lantern Echo.app', daysAgo(11))],
            sourcePackage: passedSource('source-lantern', 'unity', '2022.3', 88_000_000, 540, daysAgo(12)),
          },
        ],
        quizzes: [],
        showcase: { submission: null },
      },
    ],
    catalog: [
      {
        projectId: 'project-tide',
        releaseId: 'rel-tide',
        appId: 48130,
        title: 'Tide Pool',
        description: 'Walk the rocks between waves and leave a mark for the next player.',
        version: '1.1.0',
        visibility: 'organization',
        publishedAt: daysAgo(21),
        unit: game210,
        platforms: ['windows-x64'],
        remixable: true,
        credits: [
          { name: 'Avery Patel', contribution: 'Design' },
          { name: 'Sam Nguyen', contribution: 'Art' },
        ],
        contentNotes: null,
        parentReleaseId: null,
        withdrawn: false,
      },
      {
        projectId: 'project-paper',
        releaseId: 'rel-paper',
        appId: 48131,
        title: 'Paper Boats',
        description: 'Fold a boat, set it on the current, and see which dock it reaches.',
        version: '1.0.0',
        visibility: 'organization',
        publishedAt: daysAgo(15),
        unit: game101,
        platforms: ['mac-arm64'],
        remixable: false,
        credits: [{ name: 'Robin Shaw', contribution: 'Design' }],
        contentNotes: 'Flashing lights in the last minute.',
        parentReleaseId: null,
        withdrawn: false,
      },
      {
        projectId: 'project-pier',
        releaseId: 'rel-pier',
        appId: 48132,
        title: 'North Pier',
        description: 'A shorter crossing, remixed from Harbor Light.',
        version: '1.0.0',
        visibility: 'organization',
        publishedAt: daysAgo(4),
        unit: game101,
        platforms: ['windows-x64'],
        remixable: false,
        credits: [
          { name: 'Harbor Light', contribution: 'Original 1.2.0', permanent: true },
          { name: 'Sam Nguyen', contribution: 'Remix' },
        ],
        contentNotes: null,
        parentReleaseId: 'rel-harbor-12',
        withdrawn: false,
      },
    ],
  };
}
